import styled from 'styled-components';
import { theme } from '@theme';

// ========== TOP BAR ==========

export const TopBar = styled.header`
  height: ${theme.layout.topBarHeight};
  background-color: ${theme.colors.background};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${theme.spacing.lg};
  position: sticky;
  top: 0;
  z-index: 100;
`;

export const TopBarLogo = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

export const TopBarSearchContainer = styled.div`
  flex: 1;
  max-width: 500px;
  margin: 0 ${theme.spacing.xl};
  position: relative;
`;

export const TopBarSearchInput = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: 0 ${theme.spacing.md} 0 ${theme.spacing['2xl']};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  background-color: ${theme.colors.white};
  transition: border-color ${theme.transitions.fast};

  &:focus {
    border-color: ${theme.colors.accent};
    outline: none;
  }

  &::placeholder {
    color: #999;
  }
`;

export const TopBarSearchIcon = styled.span`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.accent};
  font-size: ${theme.typography.sizes.lg};
`;

export const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
`;

export const TopBarIcon = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.accent};
  font-size: ${theme.typography.sizes.xl};
  cursor: pointer;
  padding: ${theme.spacing.sm};
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.accentDark};
  }
`;

export const TopBarProfile = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  cursor: pointer;
`;

// ========== SIDEBAR ==========

export const Sidebar = styled.aside`
  width: ${theme.layout.sidebarWidth};
  background-color: ${theme.colors.surface};
  height: calc(100vh - ${theme.layout.topBarHeight});
  position: sticky;
  top: ${theme.layout.topBarHeight};
  overflow-y: auto;
  padding: ${theme.spacing.lg} 0;
`;

export const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

export const SidebarItem = styled.a<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  color: ${props => props.$active ? theme.colors.primary : theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  position: relative;
  transition: background-color ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.background};
  }

  ${props => props.$active && `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background-color: ${theme.colors.accent};
    }
  `}
`;

export const SidebarIcon = styled.span`
  font-size: ${theme.typography.sizes.lg};
  color: inherit;
`;

// ========== MAIN WORKSPACE ==========

export const MainWorkspace = styled.main`
  flex: 1;
  background-color: ${theme.colors.background};
  padding: ${theme.spacing.xl};
  min-height: calc(100vh - ${theme.layout.topBarHeight});
`;

export const PageTitle = styled.h1`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
`;

export const PageSubtitle = styled.h2`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
`;

export const SectionTitle = styled.h3`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
`;

export const TextNormal = styled.p`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.normal};
  color: ${theme.colors.text};
  line-height: 1.6;
`;

export const Label = styled.label`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
  margin-bottom: ${theme.spacing.sm};
`;

// ========== CARDS ==========

export const Card = styled.div`
  background-color: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  transition: box-shadow ${theme.transitions.normal};

  &:hover {
    box-shadow: ${theme.shadows.lg};
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

export const CardTitle = styled.h4`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

export const CardBody = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

// ========== BUTTONS ==========

export const ButtonPrimary = styled.button`
  background-color: ${theme.colors.accent};
  color: ${theme.colors.background};
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover:not(:disabled) {
    background-color: ${theme.colors.accentDark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonSecondary = styled.button`
  background-color: transparent;
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover:not(:disabled) {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.white};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonTertiary = styled.button`
  background-color: transparent;
  color: ${theme.colors.text};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover:not(:disabled) {
    background-color: ${theme.colors.surface};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ========== MODALS ==========

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${theme.spacing.lg};
`;

export const ModalContainer = styled.div`
  background-color: ${theme.colors.background};
  border: 2px solid ${theme.colors.accent};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${theme.shadows.xl};
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

export const ModalTitle = styled.h2`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
`;

export const ModalCloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text};
  font-size: ${theme.typography.sizes['2xl']};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  line-height: 1;
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.accent};
  }
`;

export const ModalBody = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.md};
`;

// ========== FOOTER ==========

export const Footer = styled.footer`
  background-color: ${theme.colors.surface};
  padding: ${theme.spacing.xl} ${theme.spacing.lg};
  text-align: center;
  margin-top: ${theme.spacing['3xl']};
`;

export const FooterText = styled.p`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.sm};
`;

export const FooterLink = styled.a`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.primary};
  margin: 0 ${theme.spacing.sm};
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.accent};
  }
`;

// ========== LAYOUT ==========

export const AppLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

export const ContentLayout = styled.div`
  display: flex;
  flex: 1;
`;
